// @flow

import React, { Component } from 'react';
import Relay from 'react-relay';
import moment from 'moment';
import styler from 'react-styling';

import { Grid, Row, Col } from 'react-bootstrap/lib';
import { MeetingroomStatus, MeetingroomScheduler, CreateMeetingroomEventModal } from '../Components/index';
import CreateEventMutation from '../Mutations/CreateEventMutation';
import EndEventMutation from '../Mutations/EndEventMutation';

import getLanguage from '../libs/Languages';
import { logError } from '../libs/Logger';
import { extractEventsFromEdges, extractEvent } from '../libs/Extract';

const getStartDate : moment = () => moment().unix();
const getEndDate : moment = () => moment().endOf('day').unix();

const eventFetchFrequency = 300000;
const timeUpdateFrequency = 1000;

const style = styler `
  Container
    background-color: #0A152D
    width: 100%
    height: 100%
    font-size: 22px
    position: fixed
    top: 0
    left: 0
    z-index: 1000
  Status
    height: 91vh
    margin-top: 9vh
`;

class MeetingroomDetailContainer extends Component {

  state: {
    clock: Object,
    newEvent: Object,
  };

  state = {
    clock: moment(),
    newEvent: null,
    endEvent: null,
  };

  componentDidMount() {
    this._eventsPoller = this._update();
    this._timePoller = this._clockUpdate();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.relay.variables.id !== this.props.relay.variables.id) {
      this.setState({
        newEvent: null,
        endEvent: null,
      });
    }
  }

  componentWillUnmount() {
    clearInterval(this._eventsPoller);
    clearInterval(this._timePoller);
  }

  _eventsPoller: number;
  _timePoller: number;

  props: {
    relay: Object,
    viewer: Object,
  };

  _handleEndMeeting = (event: Object) => {
    const timezone = moment.tz.guess();
    this.props.relay.commitUpdate(
      new EndEventMutation({
        event,
        timezone,
      }),
      {
        onSuccess: (response) => {
          this.setState({
            endEvent: response.endEvent.event,
            newEvent: null,
          });
        },
        onFailure: (error) => { logError(error); },
      }
    );
  }

  _handleRoomBook = (eventData: Object, innerCallbacks: Function) => {
    const callbacks = {
      onError: innerCallbacks.onError,
      onSuccess: (response) => {
        this.setState({
          newEvent: response.createEvent.event,
        });
        innerCallbacks.onSuccess(response);
      },
    };
    // eslint-disable-next-line
    this.props.relay.commitUpdate(
      new CreateEventMutation({
        eventData,
      }),
      callbacks,
    );
  }

  // messy; but required when creating new events as there are delays when fetching back from the microsoft API.
  // for that the only stable id after creation response is the iCalUId
  _addEventIfMissing = (events: Array<Object>, event: ?Object) => {
    if (!event) return events;
    // $FlowFixMe
    const eventIsMissing = events.filter(e => e.iCalUId === event.iCalUId).length === 0;
    if (eventIsMissing) {
      // even though the event list is sorted, there might be events that began on the past until the next
      // event fetch occurs
      const newEvent = extractEvent({ node: event });
      events.unshift(newEvent);
      return events.sort((a, b) => a.start.diff(b.start));
    }
    // no point keeping the event in state, avoid search afterwards
    this.setState({
      newEvent: null,
    });
    return events;
  }

  // this is the inverse of addEventIfMissing, also needed due to microsoft API delay after event updates
  _removeEvent = (events: Array<Object>, event: ?Object) => {
    if (!event) return events;
    // $FlowFixMe
    const updatedEvents = events.filter(e => e.iCalUId !== event.iCalUId);
    // do not remove event from state; it will be cleaned eventually and if we do that to eagerly then
    // we risk to get it back due to MS delay.
    return updatedEvents;
  }

  _convertEvents = (meetingRoom: Object, newEvent: ?Object, endEvent: ?Object) => {
    let events = extractEventsFromEdges(meetingRoom.events.edges);
    events = this._addEventIfMissing(events, newEvent);
    events = this._removeEvent(events, endEvent);
    return events;
  };

  _update = () => (
    setInterval(() => {
      this.props.relay.forceFetch({
        startDate: getStartDate(),
        endDate: getEndDate(),
      });
    }, eventFetchFrequency)
  );

  _clockUpdate = () => (
    setInterval(() => {
      this.setState({
        clock: moment(),
      });
    }, timeUpdateFrequency)
  );

  render() {
    const { clock, newEvent, endEvent } = this.state;
    const { meetingRoom } = this.props.viewer;
    const events = this._convertEvents(meetingRoom, newEvent, endEvent);
    const lang = getLanguage();
    return (
      <Grid style={style.Container}>
        <Row>
          <Col sm={9} style={style.Status}>
            <MeetingroomStatus
              viewer={this.props.viewer}
              meetingroom={meetingRoom}
              events={events}
              clock={clock}
              lang={lang}
              show={this.props.relay.variables.show}
              onEndMeeting={this._handleEndMeeting}
              onBook={this._handleRoomBook}
            />
          </Col>
          <Col sm={3}>
            <MeetingroomScheduler
              meetingroom={meetingRoom}
              events={events}
              clock={clock}
              lang={lang}
            />
          </Col>
        </Row>
      </Grid>
    );
  }

}

export default Relay.createContainer(MeetingroomDetailContainer, {
  initialVariables: {
    id: null,
    startDate: getStartDate(),
    endDate: getEndDate(),
    searchContact: false,
  },
  fragments: {
    viewer: (vars) => Relay.QL`
      fragment on Viewer {
        meetingRoom(id: $id) {
          id
          displayName
          email
          events (first: 1000, startDate: $startDate, endDate: $endDate) {
            edges {
              node {
                id
                iCalUId
                subject
                body
                startDate
                endDate
                occupancy
                organizer {
                  displayName
                  email
                }
                requiredAttendees {
                  displayName
                  email
                }
                optionalAttendees {
                  displayName
                  email
                }
                categories {
                  name
                }
              }
            }
          }
        }
        ${CreateMeetingroomEventModal.getFragment('viewer', vars.searchContact)},
      }
    `,
  },
});



// WEBPACK FOOTER //
// ./src/js/Containers/MeetingroomDetailContainer.js